import React, { useEffect, useState } from 'react';
import {
  Modal, Form, Input, Select,
  Row, Col, Button, Typography, message, Spin
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import httpClient from '../../utils/HttpClient';
import { BASIC_INFO_URL } from '../../constants/Url';
import dataAddress from '../../utils/address.json';
import PhoneInput from '../InputField/PhoneInput';

const { Title } = Typography;

const EditProfileModal = ({ open, profile, avatarUrl, onClose, debouncedFetchAll }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.authReducer);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [localAvatar, setLocalAvatar] = useState(avatarUrl);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('86');

  // Province / City options
  const provinceOptions = dataAddress?.map(item => ({
    value: item.text,
    label: item.text
  }));

  const selectedProvince = Form.useWatch("province", form);

  const cityOptions = dataAddress
    ?.find(item => item.text === selectedProvince)
    ?.children?.map(city => ({
      value: city.text,
      label: city.text
    })) || [];

  // ✅ Set form values when modal opens
  useEffect(() => {
    if (open && profile) {
      form.setFieldsValue({
        memberId: profile?.memberId,
        name: profile?.name,
        gender: profile?.sex,
        province: profile?.province,
        city: profile?.city,
        category: profile?.category,
        email: profile?.email,
      });
      setPhone(profile?.phone?.number || '');
      setEmail(profile?.email || '');
      setCountryCode(String(profile?.phone?.countryCode || '86'));
      setLocalAvatar(avatarUrl);
    }
  }, [open, profile, avatarUrl, form]);

  // ✅ Save handler
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);

      const payload = {};

      // ✅ compare and only add changed fields

      // if (values.memberId !== profile?.memberId) {
      //   payload.memberId = values.memberId;
      // }

      if (values.name !== profile?.name) {
        payload.name = values.name;
      }

      if (values.gender !== profile?.sex) {
        payload.sex = values.gender;
      }

      if (values.category !== profile?.category) {
        payload.category = values.category;
      }

      if (values.city !== profile?.city) {
        payload.city = values.city;
      }

      if (values.province !== profile?.province) {
        payload.province = values.province;
      }

      // phone comparison
      // const fullPhone = {
      //   countryCode: Number(countryCode),
      //   number: Number(phone),
      // };

      // if (
      //   fullPhone.countryCode !== profile?.phone?.countryCode ||
      //   fullPhone.number !== profile?.phone?.number
      // ) {
      //   payload.phone = fullPhone;
      // }

      // ❗ If nothing changed
      if (Object.keys(payload).length === 0) {
        setLoading(false);
        return;
      }

      const res = await httpClient.put(BASIC_INFO_URL, payload);

      if (res?.data?.status === 200) {
        message.success(t("Profile updated successfully"));
        debouncedFetchAll();
        onClose();
      } else {
        message.error(res?.data?.message || t("Update failed"));
      }

    } catch (error) {
      console.error(error);
      message.error(t("Please fill in all required fields"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      width={660}
      footer={null}
    >
      <Spin spinning={loading}>
      {/* Header */}
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Title level={4}>{t("Edit Profile")}</Title>
      </div>

      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="memberId"
              label={t("MemberID")}
            >
              <Input size="large" disabled/>
            </Form.Item>
          </Col>
          {profile?.phone?.number && (
            <Col span={12}>
              <Form.Item label={t("Phone Number")}>
                <PhoneInput
                  phone={phone}
                  disabled={true}
                  setPhone={(val) => {
                    setPhone(val);
                  }}
                  countryCode={countryCode}
                  setCountryCode={(val) => {
                    setCountryCode(val);
                  }}
                  t={t}
                />
              </Form.Item>
            </Col>
          )}
          <Col span={12}>
            <Form.Item
              name="name"
              label={t("Name")}
              rules={[{ required: true, message: t("text_enter_name") }]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>

          {profile?.email && (
            <Col span={12}>
              <Form.Item
                name="email"
                label={t("text_email")}
              >
                <Input size="large" disabled={true} />
              </Form.Item>
            </Col>
          )}

          <Col span={12}>
            <Form.Item
              name="gender"
              label={t("gender")}
              rules={[{ required: true, message: t("text_select_gender") }]}
            >
              <Select
                size="large"
                options={[
                  { value: 'Male', label: t('Male') },
                  { value: 'Female', label: t('Female') },
                ]}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="province"
              label={t("Province")}
              rules={[{ required: true, message: t("text_select_province") }]}
            >
              <Select
                size="large"
                options={provinceOptions}
                onChange={() => form.setFieldsValue({ city: null })}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="city"
              label={t("City")}
              rules={[{ required: true, message: t("text_select_city") }]}
            >
              <Select
                size="large"
                options={cityOptions}
              />
            </Form.Item>
          </Col>

          <Col span={
            (!!profile?.phone?.number !== !!profile?.email) ? 24 : 12
          }>
            <Form.Item
              name="category"
              label={t("Category")}
              rules={[{ required: true, message: t("text_select_category") }]}
            >
              <Select
                size="large"
                options={[
                  { value: 'Customer', label: t('Customer') },
                  { value: 'Professional Stylist', label: t('Professional Stylist') },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Button
          type="primary"
          size="large"
          block
          loading={loading}
          onClick={handleSave}
        >
          {t("Save")}
        </Button>
      </Form>
    </Spin>
    </Modal>
  );
};

export default EditProfileModal;